import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import type { LogoCornersResponse } from '../types/logoPlacement';

/**
 * Loads the logo-corner scores for a logo-free post — a StaticImage post's
 * only image, or one Carousel slide (each slide's corner is independent, so
 * pass slideNumber to scope the fetch/apply to that specific slide).
 *
 * Refetches only when the post, slide, or the raw image's S3 *object*
 * changes — the presigned query string is ignored, so a freshly re-signed
 * URL for the same image doesn't trigger another request. No polling.
 */
export function useLogoCorners(
  postId: string | null | undefined,
  rawImageUrl: string | null | undefined,
  enabled: boolean,
  isCarousel: boolean = false,
  slideNumber?: number | null,
) {
  const [data, setData] = useState<LogoCornersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const rawKey = rawImageUrl ? rawImageUrl.split('?')[0] : '';

  useEffect(() => {
    setData(null);
    setError(null);
    if (!enabled || !postId || !rawKey || (isCarousel && !slideNumber)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const request = isCarousel
      ? apiService.getCarouselLogoCorners(postId, slideNumber as number)
      : apiService.getLogoCorners(postId);
    request
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Could not load logo placement options.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [postId, rawKey, enabled, isCarousel, slideNumber]);

  return { data, setData, error, loading };
}
